import { BasePaymentProvider } from '../base';
import { PaymentsConfig } from '../../shared/types/config';
import { CheckoutParams, CheckoutSession } from '../../shared/types/checkout';
import { Customer, CustomerCreateParams } from '../../shared/types/customer';
import {
  Subscription,
  SubscriptionCreateParams,
  SubscriptionUpdateParams,
  SubscriptionStatus,
} from '../../shared/types/subscription';
import { PaymentsError, PaymentErrorType } from '../../shared/types/errors';
import Stripe from 'stripe';

export class StripePaymentProvider extends BasePaymentProvider {
  public name = 'stripe';
  private stripeClient: Stripe;

  constructor(config: PaymentsConfig) {
    super(config);
    if (!config.stripeConfig?.publishableKey) {
      throw new PaymentsError(
        PaymentErrorType.CONFIGURATION_ERROR,
        'Stripe publishable key is missing in config.'
      );
    }
    if (!process.env.STRIPE_SECRET_KEY) {
      throw new PaymentsError(
        PaymentErrorType.CONFIGURATION_ERROR,
        'STRIPE_SECRET_KEY environment variable is not set.'
      );
    }
    this.stripeClient = new Stripe(process.env.STRIPE_SECRET_KEY, {
      apiVersion: '2025-02-24.acacia',
    });
  }

  public async initialize(): Promise<void> {
    // No specific client-side initialization needed for server-side Stripe operations

    console.log('StripePaymentProvider initialized (server-side)');
  }

  public async createCheckoutSession(
    params: CheckoutParams
  ): Promise<CheckoutSession> {
    try {
      const session = await this.stripeClient.checkout.sessions.create({
        payment_method_types: ['card'],
        line_items: [
          {
            price: params.priceId,
            quantity: 1,
          },
        ],
        mode: params.mode || 'payment',
        success_url:
          params.successUrl ||
          `${process.env.NEXT_PUBLIC_BASE_URL}/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url:
          params.cancelUrl || `${process.env.NEXT_PUBLIC_BASE_URL}/cancel`,
        customer_email: params.customerEmail,
        allow_promotion_codes: params.allowPromotionCodes,
        metadata: params.metadata,
      });

      if (!session.url) {
        throw new PaymentsError(
          PaymentErrorType.UNKNOWN_ERROR,
          'Stripe checkout session URL is missing.'
        );
      }

      return {
        id: session.id,
        url: session.url,
        status: session.status as CheckoutSession['status'],
        amountTotal: session.amount_total || undefined,
        currency: session.currency || undefined,
        customerEmail: session.customer_details?.email || undefined,
        subscriptionId: session.subscription?.toString() || undefined,
      };
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  public async createCustomer(params: CustomerCreateParams): Promise<Customer> {
    try {
      const customer = await this.stripeClient.customers.create({
        email: params.email,
        name: params.name,
        metadata: params.metadata,
      });
      return {
        id: customer.id,
        email: customer.email || params.email,
        name: customer.name || params.name,
        phone: null, // Stripe API does not directly return phone for customer creation
        stripeCustomerId: customer.id,
        subscriptions: [], // Subscriptions are fetched separately
        paymentMethods: [], // Payment methods are fetched separately
        defaultPaymentMethodId: null, // Default payment method is set separately
        metadata: customer.metadata,
        created: new Date(customer.created * 1000),
        updated: new Date(customer.created * 1000), // Stripe customer object doesn't have an 'updated' timestamp directly
      };
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  public async retrieveCustomer(customerId: string): Promise<Customer | null> {
    try {
      const customer = await this.stripeClient.customers.retrieve(customerId);
      if (customer.deleted) {
        return null;
      }
      return {
        id: customer.id,
        email: customer.email || '',
        name: customer.name || undefined,
        phone: customer.phone || undefined,
        stripeCustomerId: customer.id,
        subscriptions: [], // Subscriptions are fetched separately
        paymentMethods: [], // Payment methods are fetched separately
        defaultPaymentMethodId:
          customer.invoice_settings?.default_payment_method?.toString() ||
          undefined,
        metadata: customer.metadata,
        created: new Date(customer.created * 1000),
        updated: new Date(customer.created * 1000), // Stripe customer object doesn't have an 'updated' timestamp directly
      };
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  public async createSubscription(
    params: SubscriptionCreateParams
  ): Promise<Subscription> {
    try {
      const subscription = await this.stripeClient.subscriptions.create({
        customer: params.customerId,
        items: (
          params.items ||
          (params.priceId ? [{ priceId: params.priceId, quantity: 1 }] : [])
        ).map((item: { priceId: string; quantity?: number }) => ({
          price: item.priceId,
          quantity: item.quantity || 1,
        })),
        trial_period_days: params.trialPeriodDays,
        metadata: params.metadata,
        expand: ['latest_invoice.payment_intent'],
      });

      return this.mapStripeSubscriptionToSubscription(subscription);
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  public async cancelSubscription(subscriptionId: string): Promise<void> {
    try {
      await this.stripeClient.subscriptions.cancel(subscriptionId);
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  public async reactivateSubscription(subscriptionId: string): Promise<void> {
    try {
      const subscription =
        await this.stripeClient.subscriptions.retrieve(subscriptionId);
      if (
        subscription.status === SubscriptionStatus.CANCELED &&
        subscription.cancel_at_period_end
      ) {
        await this.stripeClient.subscriptions.update(subscriptionId, {
          cancel_at_period_end: false,
        });
      } else {
        throw new PaymentsError(
          PaymentErrorType.VALIDATION_ERROR,
          'Subscription cannot be reactivated.'
        );
      }
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  public async retrieveCheckoutSession(
    sessionId: string
  ): Promise<CheckoutSession> {
    try {
      const session =
        await this.stripeClient.checkout.sessions.retrieve(sessionId);
      if (!session.url) {
        throw new PaymentsError(
          PaymentErrorType.UNKNOWN_ERROR,
          'Stripe checkout session URL is missing.'
        );
      }
      return {
        id: session.id,
        url: session.url,
        status: session.status as CheckoutSession['status'],
        amountTotal: session.amount_total || undefined,
        currency: session.currency || undefined,
        customerEmail: session.customer_details?.email || undefined,
        subscriptionId: session.subscription?.toString() || undefined,
      };
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  public async listSubscriptions(
    customerId: string,
    status?: SubscriptionStatus
  ): Promise<Subscription[]> {
    try {
      const subscriptions = await this.stripeClient.subscriptions.list({
        customer: customerId,
        status: status === SubscriptionStatus.ALL ? undefined : status,
      });
      return subscriptions.data.map(this.mapStripeSubscriptionToSubscription);
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  public async retrieveSubscription(
    subscriptionId: string
  ): Promise<Subscription | null> {
    try {
      const subscription =
        await this.stripeClient.subscriptions.retrieve(subscriptionId);
      return this.mapStripeSubscriptionToSubscription(subscription);
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  public async updateSubscription(
    subscriptionId: string,
    params: Partial<SubscriptionUpdateParams>
  ): Promise<Subscription> {
    try {
      const currentSubscription =
        await this.stripeClient.subscriptions.retrieve(subscriptionId);
      const items = params.items
        ? params.items.map((item: { priceId: string; quantity?: number }) => ({
            id: currentSubscription.items.data.find(
              i => i.price?.id === item.priceId
            )?.id,
            price: item.priceId,
            quantity: item.quantity || 1,
          }))
        : undefined;

      const subscription = await this.stripeClient.subscriptions.update(
        subscriptionId,
        {
          items,
          metadata: params.metadata,
        }
      );
      return this.mapStripeSubscriptionToSubscription(subscription);
    } catch (error: unknown) {
      throw this.handleError(error, PaymentErrorType.NETWORK_ERROR);
    }
  }

  private mapStripeSubscriptionToSubscription(
    stripeSubscription: Stripe.Subscription
  ): Subscription {
    return {
      id: stripeSubscription.id,
      customerId: stripeSubscription.customer.toString(),
      status: stripeSubscription.status as SubscriptionStatus,
      items: stripeSubscription.items.data.map(item => ({
        id: item.id,
        priceId: item.price?.id || '',
        quantity: item.quantity || 1,
      })),
      currentPeriodStart: new Date(
        stripeSubscription.current_period_start * 1000
      ),
      currentPeriodEnd: new Date(stripeSubscription.current_period_end * 1000),
      cancelAtPeriodEnd: stripeSubscription.cancel_at_period_end,
      trialStart: stripeSubscription.trial_start
        ? new Date(stripeSubscription.trial_start * 1000)
        : undefined,
      trialEnd: stripeSubscription.trial_end
        ? new Date(stripeSubscription.trial_end * 1000)
        : undefined,
      metadata: stripeSubscription.metadata,
      created: new Date(stripeSubscription.created * 1000),
      updated: new Date(stripeSubscription.created * 1000), // Stripe subscription object doesn't have an 'updated' timestamp directly
    };
  }
}
